<template>
  <div
    v-show="!hidden"
    class="fixed inset-0 z-1000 flex items-end justify-center md:items-center md:p-4"
    role="dialog"
    aria-modal="true"
    :aria-label="ariaLabel"
    @keydown="handleKeydown"
  >
    <!-- z-1000 (custom Tailwind value): above the z-500 basket bar AND the z-999 MarketingPopup,
         since a dismissible marketing ad must never paint over a required question or an upsell. -->
    <div class="fixed inset-0 bg-gray-900 bg-opacity-75 modal-sheet-scrim"></div>

    <div
      ref="panel"
      data-testid="modal-sheet-panel"
      tabindex="-1"
      class="relative w-full bg-white shadow-xl rounded-t-2xl md:rounded-xl max-h-[85vh] md:max-h-[90vh] flex flex-col overflow-hidden focus:outline-none modal-sheet-panel"
      :class="panelClass"
    >
      <slot name="header" />

      <div
        ref="scrollArea"
        :key="contentKey"
        data-testid="modal-sheet-body"
        class="flex-grow overflow-y-auto overscroll-contain"
        :class="bodyClass"
      >
        <slot />
      </div>

      <!-- Two footers, because the two ways of filling one are not interchangeable.
           A slot consumer (UpsellModal) always has content, so its footer simply shows.
           A Teleport consumer (QuestionModal) is filled after mount, so `$slots.footer` cannot
           answer the question and CSS `:empty` has to - and `:empty` is defeated by any child node,
           including the anchor nodes Vue leaves behind for an unfilled `<slot>`. Rendering the
           element with nothing inside it at all is what keeps the divider hidden until
           QuestionFooterButtons teleports its buttons in. -->
      <div v-if="$slots.footer" data-testid="modal-sheet-footer" :class="footerClass">
        <slot name="footer" />
      </div>
      <div v-else :id="footerId" data-testid="modal-sheet-footer" :class="[footerClass, 'empty:hidden']"></div>
    </div>
  </div>
</template>

<script>
import { createScrollLock } from '@/utils/bodyScrollLock'

export default {
  name: 'ModalSheet',

  props: {
    ariaLabel: {
      type: String,
      default: 'Dialog'
    },
    // Remounts the scroll area and returns it to the top, so stepping from one question (or one
    // upsell page) to the next does not inherit the previous scroll position.
    contentKey: {
      type: [String, Number],
      default: 0
    },
    panelClass: {
      type: String,
      default: 'md:max-w-xl'
    },
    // Hidden rather than unmounted: a sheet that steps aside for another dialog has to keep its
    // scroll position, its form state and its scroll lock for when it comes back.
    hidden: {
      type: Boolean,
      default: false
    },
    // Owned by the consumer so a sheet with its own fixed header can tighten the top padding the
    // header already provides.
    bodyClass: {
      type: String,
      default: 'px-4 pt-5 pb-6 md:px-6 md:pt-6'
    },
    // Lets a consumer expose the footer as a Teleport target rather than filling it via the slot.
    footerId: {
      type: String,
      default: null
    }
  },

  computed: {
    // Shared by both footer variants so the divider, padding and background can only ever drift
    // together. The inset divider is drawn via ::before to respect the horizontal padding, and
    // `:empty` ignores pseudo-elements, so it does not defeat the teleport variant's empty:hidden.
    footerClass() {
      return "flex-shrink-0 bg-white px-4 pb-5 md:px-6 md:pb-6 before:block before:border-t before:content-[''] before:mb-4"
    }
  },

  watch: {
    contentKey() {
      this.$nextTick(() => {
        this.$refs.scrollArea?.scrollTo({ top: 0 })
        this.$refs.panel?.focus({ preventScroll: true })
      })
    }
  },

  created() {
    this.scrollLock = createScrollLock()
  },

  mounted() {
    this.scrollLock.lock()
    this.$refs.panel?.focus({ preventScroll: true })
  },

  beforeUnmount() {
    this.scrollLock.unlock()
  },

  methods: {
    handleKeydown(event) {
      if (event.key !== 'Tab') return
      const panel = this.$refs.panel
      if (!panel) return
      const focusables = panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
      if (focusables.length === 0) {
        event.preventDefault()
        return
      }
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
  }
}
</script>

<style scoped>
.modal-sheet-scrim {
  animation: modal-sheet-fade 0.2s ease-out;
}

.modal-sheet-panel {
  animation: modal-sheet-rise 0.25s ease-out;
}

@keyframes modal-sheet-fade {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes modal-sheet-rise {
  from {
    opacity: 0;
    transform: translateY(1rem);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .modal-sheet-scrim,
  .modal-sheet-panel {
    animation: none;
  }
}
</style>
